/**
 * Поле поиска с подсказками.
 *
 * Индекс подгружается при первом фокусе, а не при старте страницы: большинство
 * сессий с картой поиском не пользуется, и платить за него временем первой
 * отрисовки незачем.
 */

import { KIND_LABELS, SearchIndex, type SearchResult } from "~/state/searchIndex";
import { clear, el } from "./dom";

export interface SearchBoxOptions {
  index: SearchIndex;
  onPick: (result: SearchResult) => void;
}

export function createSearchBox(options: SearchBoxOptions): HTMLElement {
  const { index, onPick } = options;

  const list = el("ul", {
    class: "search__results",
    attrs: { role: "listbox", id: "search-results" },
    dataset: { state: "empty" },
  });

  let results: SearchResult[] = [];
  let active = -1;

  const input = el("input", {
    class: "search__input",
    attrs: {
      type: "search",
      placeholder: "Адрес, улица или кадастровый номер",
      "aria-label": "Поиск по карте",
      autocomplete: "off",
      role: "combobox",
      "aria-expanded": "false",
      "aria-controls": "search-results",
    },
  });

  const status = el("p", { class: "search__status", attrs: { role: "status" } });

  const render = (): void => {
    clear(list);
    active = -1;

    if (results.length === 0) {
      list.dataset["state"] = "empty";
      input.setAttribute("aria-expanded", "false");
      return;
    }

    list.dataset["state"] = "open";
    input.setAttribute("aria-expanded", "true");

    results.forEach((result, position) => {
      list.appendChild(
        el(
          "li",
          {
            class: "search__result",
            attrs: { role: "option", id: `search-option-${position}`, "aria-selected": "false" },
            on: {
              // mousedown, а не click: blur на поле успевает закрыть список
              // раньше, чем click доедет до элемента.
              mousedown: (event) => {
                event.preventDefault();
                choose(position);
              },
            },
          },
          el("span", { class: "search__result-name", text: result.n }),
          el("span", { class: "search__result-kind", text: KIND_LABELS[result.k] }),
        ),
      );
    });
  };

  const highlight = (next: number): void => {
    const items = [...list.querySelectorAll<HTMLElement>(".search__result")];
    items.forEach((item, position) => {
      const selected = position === next;
      item.classList.toggle("search__result--active", selected);
      item.setAttribute("aria-selected", String(selected));
    });
    active = next;
    const current = items[next];
    if (current) {
      current.scrollIntoView({ block: "nearest" });
      input.setAttribute("aria-activedescendant", `search-option-${next}`);
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  };

  const choose = (position: number): void => {
    const result = results[position];
    if (!result) return;
    input.value = result.n;
    results = [];
    render();
    onPick(result);
  };

  const runSearch = (): void => {
    const query = input.value.trim();
    if (query.length < 2) {
      results = [];
      status.textContent = "";
      render();
      return;
    }

    if (index.unavailable) {
      status.textContent = "Поисковый индекс не собран. Выполните «npm run data:search».";
      return;
    }

    results = index.search(query);
    status.textContent = results.length === 0 ? "Ничего не найдено" : "";
    render();
  };

  let debounce: number | undefined;
  input.addEventListener("input", () => {
    if (debounce !== undefined) window.clearTimeout(debounce);
    debounce = window.setTimeout(runSearch, 140);
  });

  input.addEventListener("focus", () => {
    void index.load().then(() => {
      if (index.unavailable) {
        status.textContent = "Поисковый индекс не собран. Выполните «npm run data:search».";
      } else if (input.value.trim().length >= 2) {
        runSearch();
      }
    });
  });

  input.addEventListener("keydown", (event) => {
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      highlight((active + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      highlight((active - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(active >= 0 ? active : 0);
    } else if (event.key === "Escape") {
      results = [];
      render();
    }
  });

  input.addEventListener("blur", () => {
    window.setTimeout(() => {
      results = [];
      render();
    }, 120);
  });

  return el("div", { class: "search" }, input, status, list);
}
